import { useEffect, useRef, useCallback } from 'react';
import { useFutureReleases } from '@/contexts/future-releases';
import { useTransactions } from '@/contexts/transactions';
import { useTimezone } from '@/contexts/TimezoneContext';
import { RecurrenceProcessor } from '@/services/RecurrenceProcessor';
import { logger } from '@/utils/logger';

interface ProcessingState {
  isProcessing: boolean;
  lastProcessedDate: string | null;
  processedReleaseIds: Set<string>;
}

export const useRecurrenceSchedulerV2 = () => {
  const { futureReleases, updateFutureRelease } = useFutureReleases();
  const { addTransaction, transactions } = useTransactions();
  const { getCurrentDateInUserTimezone, isTimezoneReady } = useTimezone();
  
  const processingStateRef = useRef<ProcessingState>({
    isProcessing: false,
    lastProcessedDate: null,
    processedReleaseIds: new Set()
  });

  const checkForExistingTransaction = useCallback((release: any, date: string): boolean => {
    if (!transactions || transactions.length === 0) return false;
    
    // Verificar se já existe uma transação criada hoje para este lançamento
    const existingTransaction = transactions.find(t => 
      t.date === date && 
      t.description?.includes(release.name) &&
      !t.deleted &&
      // Verificar se o valor e tipo coincidem
      Math.abs(t.amount) === Math.abs(release.amount) &&
      ((release.type === 'receita' && t.amount > 0) || (release.type === 'despesa' && t.amount < 0))
    );
    
    return !!existingTransaction;
  }, [transactions]);

  const processScheduledReleases = useCallback(async () => {
    if (!isTimezoneReady || !futureReleases.length || !addTransaction || !updateFutureRelease) {
      return;
    }

    const today = getCurrentDateInUserTimezone();
    const processingState = processingStateRef.current;
    
    // Evitar execução simultânea
    if (processingState.isProcessing) {
      logger.info('[useRecurrenceSchedulerV2] Processamento já em andamento, pulando');
      return;
    }

    // Verificar se já processamos hoje
    if (processingState.lastProcessedDate === today) {
      logger.info('[useRecurrenceSchedulerV2] Já processado hoje:', today);
      return;
    }

    processingState.isProcessing = true;
    logger.info('[useRecurrenceSchedulerV2] Iniciando processamento para:', today);

    try {
      const releasesToProcess = futureReleases.filter(release => {
        // Verificar se deve processar hoje
        const shouldProcess = RecurrenceProcessor.shouldProcessTodaysRelease(release, today);
        
        // Verificar se já processamos este lançamento hoje
        const alreadyProcessedToday = processingState.processedReleaseIds.has(`${release.id}-${today}`);
        
        // Verificar se já existe transação para hoje
        const existingTransaction = checkForExistingTransaction(release, today);
        
        const canProcess = shouldProcess && !alreadyProcessedToday && !existingTransaction;
        
        if (shouldProcess) {
          logger.debug('[useRecurrenceSchedulerV2] Verificação de lançamento:', {
            id: release.id,
            name: release.name,
            shouldProcess,
            alreadyProcessedToday,
            existingTransaction,
            canProcess
          });
        }
        
        return canProcess;
      });

      logger.info(`[useRecurrenceSchedulerV2] Encontrados ${releasesToProcess.length} lançamentos para processar`);

      for (const release of releasesToProcess) {
        try {
          // Para recorrentes atrasados, processar múltiplas ocorrências
          if (release.isRecurrent && release.dueDate < today) {
            logger.info(`[useRecurrenceSchedulerV2] Processando recorrente atrasado: ${release.name}`);
            
            let currentDueDate = release.dueDate;
            const maxIterations = 100; // Limite de segurança
            let iterations = 0;
            
            // Processar todas as ocorrências até hoje
            while (currentDueDate <= today && iterations < maxIterations) {
              iterations++;
              
              // Verificar se já existe transação para esta data
              if (!checkForExistingTransaction(release, currentDueDate) && 
                  !processingState.processedReleaseIds.has(`${release.id}-${currentDueDate}`)) {
                
                logger.info(`[useRecurrenceSchedulerV2] Criando transação para ${release.name} em ${currentDueDate}`);
                await RecurrenceProcessor.createTransactionFromRelease(release, currentDueDate, addTransaction);
                processingState.processedReleaseIds.add(`${release.id}-${currentDueDate}`);
              }
              
              // Calcular próxima data
              const tempRelease = { ...release, dueDate: currentDueDate };
              const nextDate = RecurrenceProcessor.calculateNextDueDate(tempRelease);
              
              if (nextDate === currentDueDate) {
                logger.error('[useRecurrenceSchedulerV2] Loop infinito detectado, interrompendo');
                break;
              }
              
              currentDueDate = nextDate;
            }
            
            // Atualizar o release com a próxima data futura
            if (currentDueDate > today) {
              await updateFutureRelease(release.id, { 
                dueDate: currentDueDate,
                lastProcessedDate: today 
              });
            }
          } else {
            // Processar release normal (não atrasado ou único)
            logger.info(`[useRecurrenceSchedulerV2] Processando: ${release.name}`);
            
            // Marcar como processado antes de criar transação
            processingState.processedReleaseIds.add(`${release.id}-${today}`);
            
            // Criar transação
            await RecurrenceProcessor.createTransactionFromRelease(release, today, addTransaction);
            logger.info(`[useRecurrenceSchedulerV2] Transação criada para: ${release.name}`);

            // Atualizar lançamento
            if (release.isRecurrent && release.recurrence && release.recurrence !== 'unica') {
              // Para recorrentes, calcular próxima data
              const nextDueDate = RecurrenceProcessor.calculateNextDueDate(release);
              await updateFutureRelease(release.id, { 
                dueDate: nextDueDate,
                lastProcessedDate: today,
                status: 'ativo'
              });
              logger.info(`[useRecurrenceSchedulerV2] Recorrente atualizado - próxima data: ${nextDueDate}`);
            } else {
              // Para não recorrentes, marcar como pago
              await updateFutureRelease(release.id, { 
                status: 'pago',
                lastProcessedDate: today
              });
              logger.info(`[useRecurrenceSchedulerV2] Lançamento único marcado como pago`);
            }
          }
        } catch (error) {
          logger.error(`[useRecurrenceSchedulerV2] Erro ao processar ${release.name}:`, error);
          // Remover da lista de processados em caso de erro
          processingState.processedReleaseIds.delete(`${release.id}-${today}`);
        }
      }

      // Marcar que processamos hoje
      processingState.lastProcessedDate = today;
      
    } finally {
      processingState.isProcessing = false;
    }
  }, [futureReleases, addTransaction, updateFutureRelease, getCurrentDateInUserTimezone, isTimezoneReady, checkForExistingTransaction]);

  useEffect(() => {
    if (!isTimezoneReady) return;
    
    // Limpar estado quando mudamos de dia
    const today = getCurrentDateInUserTimezone();
    const processingState = processingStateRef.current;
    
    if (processingState.lastProcessedDate && processingState.lastProcessedDate !== today) {
      logger.info('[useRecurrenceSchedulerV2] Novo dia detectado, limpando estado');
      processingState.processedReleaseIds.clear();
      processingState.lastProcessedDate = null;
    }

    // Executar processamento apenas uma vez por dia
    processScheduledReleases();
    
    // Verificar a cada hora para capturar mudanças de dia
    const intervalId = setInterval(() => {
      const currentDay = getCurrentDateInUserTimezone();
      if (currentDay !== processingState.lastProcessedDate) {
        processScheduledReleases();
      }
    }, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [processScheduledReleases, isTimezoneReady, getCurrentDateInUserTimezone]);

  return { processScheduledReleases };
};